/* eslint-disable @next/next/no-img-element */
"use client";
import { useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform, AnimatePresence } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useViewportHeight } from "@/app/hooks/useViewportHeight";

const SPRING_CONFIG = {
    lift: { stiffness: 220, damping: 34, mass: 0.2 },
    opacity: { stiffness: 260, damping: 36, mass: 0.22 },
} as const;

const SCROLL_KEYFRAMES = {
    liftY: [0, 0.16, 0.65, 1] as const,
    opacity: [0, 0.2] as const,
};

const SERVICES = [
    {
        icon: '/img/dsgvo.webp',
        alt: 'DSGVO',
        title: 'DSGVO konforme Webseiten',
        description: 'Dank europäischer Tools und Hosting innerhalb der EU sind Websites auch ohne Cookie-Banner möglich!',
    },
    {
        icon: '/img/love.webp',
        alt: 'Marke',
        title: 'Markenauftritt, der bleibt',
        description: 'Ein Markenauftritt, der bleibt, schafft Vertrauen und bleibt im Gedächtnis. Wir entwickeln eine klare, authentische Markenidentität, die deine Werte widerspiegelt und deine Zielgruppe begeistert – sowohl online als auch offline.',
    },
    {
        icon: '/img/modern.webp',
        alt: 'Tools',
        title: 'Modernste Tools & Frameworks',
        description: 'Ob Webflow, Wordpress oder handgemachter Code – ich finde die perfekte Lösung für dein Projekt!',
    },
    {
        icon: '/img/money.webp',
        alt: 'SEO',
        title: 'Kosteneffiziente Webseiten!',
        description: 'Mit meiner Webseitenerstellung kann Geld gespart werden, indem unnötige Kosten vermieden und effiziente Lösungen genutzt werden!',
    },
] as const;

// Generiere Sterne mit zufälligen aber konsistenten Werten - 117 für Desktop/Tablet, 71 für Mobile
const generateStars = (count: number) => Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${(i * 8.47) % 97 + 1}%`,
    top: `${(i * 7.3) % 95 + 2}%`,
    size: [4, 5, 6, 7, 8][(i * 3) % 5],
    color: i % 2 === 0 ? '#FFE066' : '#5ba0ff',
    duration: 1.5 + (i % 10) * 0.3,
    delay: (i * 0.15) % 8,
    maxOpacity: 0.4 + ((i % 6) * 0.1),
}));

const STARS_DESKTOP = generateStars(117);
const STARS_MOBILE = generateStars(71);

interface ServiceCardProps {
    icon: string;
    alt: string;
    title: string;
    description: string;
}

const ServiceCard = ({ icon, alt, title, description }: ServiceCardProps) => (
    <motion.div 
        className="relative z-[1] flex flex-col items-center gap-3 w-full max-w-[var(--card-max-width)] px-4 py-4 rounded-2xl border-[2px] border-[#5ba0ff] sm:flex-row sm:gap-5 sm:px-6 lg:gap-6 lg:px-7 lg:py-[18px] lg:rounded-[24px] lg:border-[3px] overflow-hidden shadow-[6px_6px_0_rgba(91,160,255,0.5)] hover:shadow-[8px_8px_0_rgba(91,160,255,0.7)]"
        whileHover={{ scale: 1.02, y: -6 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={{
            background: 'linear-gradient(135deg, rgba(29,43,79,0.75) 0%, rgba(29,43,79,0.65) 50%, rgba(6,15,32,0.7) 100%)',
            backdropFilter: 'blur(12px)',
        }}
    >
        {/* Inner Glow */}
        <div className="absolute inset-[3px] lg:inset-[4px] rounded-2xl lg:rounded-[20px] bg-gradient-to-br from-[#5ba0ff]/15 via-transparent to-transparent pointer-events-none" />
        
        {/* Subtle shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#5ba0ff]/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <img src={icon} alt={alt} className="relative z-[2] w-[var(--icon-size)] h-auto drop-shadow-[0_4px_12px_rgba(91,160,255,0.4)]" loading="lazy" />
        <div className="relative z-[2]">
            <h3 
                className="text-[16px] lg:text-[19px] font-mediumbold text-white font-[luckiestguy] text-center sm:text-left mb-1 leistungen2-card-title"
                style={{
                    textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7), 0 0 12px rgba(0,0,0,0.5), 1px 1px 0 rgba(0,0,0,1), -1px -1px 0 rgba(0,0,0,1), 1px -1px 0 rgba(0,0,0,1), -1px 1px 0 rgba(0,0,0,1)'
                }}
            >
                {title}
            </h3>
            <p className="text-[13px] lg:text-[15px] leading-[20px] lg:leading-[23px] text-[#AED1FF] font-[Poppins] font-medium text-center sm:text-left leistungen2-card-text">
                {description}
            </p>
        </div>
    </motion.div>
);

const Leistungen2 = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const viewportHeight = useViewportHeight();
    const [currentCardIndex, setCurrentCardIndex] = useState(0);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });

    const liftY = useTransform(
        scrollYProgress,
        [...SCROLL_KEYFRAMES.liftY],
        [viewportHeight, 0, 0, -viewportHeight],
        { clamp: false }
    );

    const opacity = useTransform(
        scrollYProgress, 
        [...SCROLL_KEYFRAMES.opacity], 
        [0, 1], 
        { clamp: false }
    );

    const smoothLiftY = useSpring(liftY, SPRING_CONFIG.lift);
    const smoothOpacity = useSpring(opacity, SPRING_CONFIG.opacity);

    const nextCard = () => {
        setCurrentCardIndex((prev) => (prev + 1) % SERVICES.length);
    };

    const prevCard = () => {
        setCurrentCardIndex((prev) => (prev - 1 + SERVICES.length) % SERVICES.length);
    };

    return (
        <section ref={sectionRef} className="relative w-full" style={{ background: 'var(--night-sky)' }}>
            <span id="leistungen2" className="block h-0 w-0" aria-hidden="true" />
            <motion.div
                initial={false}
                className="fixed inset-0 h-screen w-full z-[75] pointer-events-none"
                style={{ y: smoothLiftY, opacity: smoothOpacity, background: 'var(--gradient-beige)' }}
            >
                <div className="absolute inset-0" style={{ background: 'var(--night-sky)' }} />
                
                {/* Blinking Stars - 117 Desktop/Tablet, 71 Mobile - Optimized with CSS */}
                {/* Desktop/Tablet Stars */}
                <div className="hidden md:block absolute inset-0 overflow-hidden pointer-events-none z-[1]">
                    {STARS_DESKTOP.map((star) => (
                        <svg
                            key={star.id}
                            className={`absolute w-${star.size} h-${star.size}`}
                            style={{ 
                                left: star.left, 
                                top: star.top,
                                opacity: 0,
                                animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
                                willChange: 'opacity',
                                '--max-opacity': star.maxOpacity,
                            } as React.CSSProperties}
                            viewBox="0 0 24 24"
                            fill={star.color}
                        >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                    ))}
                </div>
                
                {/* Mobile Stars */}
                <div className="md:hidden absolute inset-0 overflow-hidden pointer-events-none z-[1]">
                    {STARS_MOBILE.map((star) => (
                        <svg
                            key={star.id}
                            className={`absolute w-${star.size} h-${star.size}`}
                            style={{ 
                                left: star.left, 
                                top: star.top,
                                opacity: 0,
                                animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
                                willChange: 'opacity',
                                '--max-opacity': star.maxOpacity,
                            } as React.CSSProperties}
                            viewBox="0 0 24 24"
                            fill={star.color}
                        >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                    ))}
                </div>
                
                <div className="w-full h-full flex flex-col items-center justify-start overflow-hidden pointer-events-auto">
                    <section className="w-full min-h-screen flex items-center justify-center flex-col pt-[var(--section-padding-y)] pb-[30px] md:pb-[var(--section-padding-y)] xl:pb-40 z-[5]">
                        <div className="w-[90%] max-w-[1200px] flex flex-col items-center justify-between gap-2 xl:gap-8 mx-auto xl:flex-row xl:items-center xl:justify-center xl:gap-12 ">
                            <div className="w-full max-w-[620px] xl:max-w-[360px] flex flex-col gap-2 lg:gap-4 relative text-center xl:gap-5 xl:text-left xl:justify-center">
                                <h2 className="text-3xl sm:text-4xl lg:text-6xl leading-tight text-[var(--yellow)] stroke-text font-luckiest z-[1] leistungen2-title">
                                    Magie für deine Marke
                                </h2>
                                <p className="hidden md:block text-sm lg:text-xl leading-[1.3] text-[var(--pastel-blue)] font-[Poppins] font-medium z-[1] leistungen2-text">
                                    Magie für deine Marke bedeutet, etwas Einzigartiges zu schaffen, das deine Zielgruppe
                                    verzaubert. Wir entwickeln eine unverwechselbare Markenidentität, die deine Vision lebendig
                                    macht und für einen bleibenden Eindruck sorgt.
                                </p>
                                <p className="md:hidden text-sm leading-[1.3] text-[var(--pastel-blue)] font-[Poppins] font-medium z-[1]">
                                    Einzigartiges Design, das verzaubert und einen bleibenden Eindruck hinterlässt.
                                </p>
                            </div>
                            
                            {/* Pfeil im Background - außerhalb des Text-Containers */}
                            <img 
                                src="/img/arrow.webp" 
                                alt="magischer Pfeil" 
                                className="block absolute top-[120px] left-[5%] xl:left-[10%] 2xl:left-[20%] h-[820px] opacity-40 pointer-events-none"
                                style={{ zIndex: 0 }}
                                loading="lazy"
                            />

                            {/* Mobile Carousel - nur eine Karte */}
                            <div className="md:hidden w-full max-w-[320px] flex flex-col items-center gap-3 mt-1">
                                <div className="relative w-full overflow-hidden min-h-[280px] flex items-center justify-center">
                                    <AnimatePresence mode="wait" initial={false}>
                                        <motion.div
                                            key={currentCardIndex}
                                            initial={{ x: 300, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            exit={{ x: -300, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                            className="w-full"
                                        >
                                            <ServiceCard {...SERVICES[currentCardIndex]} />
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                                
                                {/* Navigation Controls */}
                                <div className="flex items-center gap-3">
                                    <motion.button
                                        onClick={prevCard}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="p-3 rounded-full bg-[#5ba0ff] border-3 border-[#1d2b4f] shadow-[4px_4px_0_rgba(29,43,79,0.3)] text-white"
                                        aria-label="Vorherige Karte"
                                    >
                                        <ChevronLeft size={24} />
                                    </motion.button>
                                    
                                    <div className="flex gap-2">
                                        {SERVICES.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentCardIndex(index)}
                                                className={`w-2 h-2 rounded-full transition-all ${
                                                    index === currentCardIndex 
                                                        ? 'bg-[#5ba0ff] w-6' 
                                                        : 'bg-[#1d2b4f]/30'
                                                }`}
                                                aria-label={`Zu Karte ${index + 1} springen`}
                                            />
                                        ))}
                                    </div>
                                    
                                    <motion.button
                                        onClick={nextCard}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="p-3 rounded-full bg-[#5ba0ff] border-3 border-[#1d2b4f] shadow-[4px_4px_0_rgba(29,43,79,0.3)] text-white"
                                        aria-label="Nächste Karte"
                                    >
                                        <ChevronRight size={24} />
                                    </motion.button>
                                </div>
                            </div>

                            {/* Desktop - alle Karten */}
                            <div className=" hidden md:flex w-full max-w-[600px] big-screen flex-col gap-3 mt-2 lg:mt-6">
                                {SERVICES.map((service) => (
                                    <ServiceCard key={service.title} {...service} />
                                ))}
                            </div>
                        </div>
                    </section>
                    
                    {/* Wave SVG */}
                    <div className="absolute bottom-0 left-0 w-full h-[var(--wave-height)] pointer-events-none z-[0]">
                        <div className="absolute inset-0" style={{ background: 'var(--night-sky)' }} />
                        <div 
                            className="absolute inset-0 bg-cover bg-no-repeat"
                            style={{ backgroundImage: "url('/img/wave2.svg')" }}
                        />
                    </div>
                </div>
            </motion.div>
            <div className="h-[120vh]" aria-hidden="true" />
        </section>
    );
};

export default Leistungen2;